import { useEffect, useState, useCallback } from "react";
import { Boxes, AlertTriangle, Search, Pencil, History as HistoryIcon, X, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const STATUS_STYLE = {
  in_stock: "bg-emerald-100 text-emerald-700", low_stock: "bg-amber-100 text-amber-700",
  out_of_stock: "bg-rose-100 text-rose-700", paused: "bg-slate-200 text-slate-600", unpublished: "bg-slate-100 text-slate-500",
};
const STATUS_LABEL = { in_stock: "In stock", low_stock: "Low stock", out_of_stock: "Out of stock", paused: "Paused", unpublished: "Unpublished" };
const REASONS = [["new_stock", "New stock"], ["manual_correction", "Manual correction"], ["damaged_stock", "Damaged"], ["lost_stock", "Lost"], ["returned_stock", "Returned"], ["inventory_count", "Inventory count"], ["other", "Other"]];
const FILTERS = [["all", "All"], ["in_stock", "In stock"], ["low_stock", "Low stock"], ["out_of_stock", "Out of stock"]];

export default function InventoryPage() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [adjust, setAdjust] = useState(null);   // row being adjusted
  const [histFor, setHistFor] = useState(null); // row for history
  const [history, setHistory] = useState(null);

  const load = useCallback(() => {
    const qp = new URLSearchParams();
    if (filter !== "all") qp.set("status", filter);
    if (q) qp.set("q", q);
    api.get(`/marketplace/seller/inventory?${qp.toString()}`).then((r) => setData(r.data)).catch(() => setData({ summary: {}, rows: [] }));
  }, [filter, q]);
  useEffect(() => { load(); }, [load]);

  const openHistory = async (row) => {
    setHistFor(row); setHistory(null);
    try { const r = await api.get(`/marketplace/seller/inventory/${row.product_id}/history`); setHistory(r.data.history || []); }
    catch { setHistory([]); }
  };

  const s = data?.summary || {};
  const rows = data?.rows || [];

  return (
    <div data-testid="inventory-page">
      <PageHeader title="Inventory" icon={Boxes} subtitle="Your single source of truth for stock — physical, reserved and available. Every change is recorded in the inventory ledger." />

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="inv-summary">
        {[["Total", s.total, "text-slate-800"], ["In stock", s.in_stock, "text-emerald-600"], ["Low stock", s.low_stock, "text-amber-600"], ["Out of stock", s.out_of_stock, "text-rose-600"], ["Reserved units", s.reserved_units, "text-blue-600"]].map(([l, v, c]) => (
          <div key={l} className="card-soft p-4"><p className="text-xs text-slate-500">{l}</p><p className={`text-2xl font-bold ${c}`}>{v ?? 0}</p></div>
        ))}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        {FILTERS.map(([v, l]) => <button key={v} onClick={() => setFilter(v)} data-testid={`inv-filter-${v}`} className={`rounded-full px-3 py-1.5 text-xs font-medium ${filter === v ? "brand-gradient-bg text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>{l}</button>)}
        <div className="relative ml-auto"><Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or SKU" className="w-56 pl-9" data-testid="inv-search" /></div>
      </div>

      {data === null ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-14 animate-pulse rounded-lg bg-white/70" />)}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-500" data-testid="inventory-empty">No products match this view.</div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="inventory-list">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-400">
              <tr><th className="p-3">Product</th><th className="p-3">SKU</th><th className="p-3 text-center">Stock</th><th className="p-3 text-center">Reserved</th><th className="p-3 text-center">Available</th><th className="p-3 text-center">Threshold</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.product_id} className="border-t border-slate-100" data-testid={`inv-${p.product_id}`}>
                  <td className="p-3"><p className="font-medium text-slate-800">{p.name}</p>{p.has_variants && <span className="text-[10px] text-slate-400">has variants</span>}</td>
                  <td className="p-3 font-mono text-xs text-slate-500">{p.sku}</td>
                  <td className="p-3 text-center font-semibold">{p.stock}</td>
                  <td className="p-3 text-center text-blue-600">{p.reserved}</td>
                  <td className="p-3 text-center font-bold">{p.available}</td>
                  <td className="p-3 text-center text-slate-500">{p.threshold}</td>
                  <td className="p-3"><span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_STYLE[p.status]}`}>{p.status === "low_stock" && <AlertTriangle className="h-3 w-3" />}{STATUS_LABEL[p.status]}</span></td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setAdjust({ ...p, mode: "new", value: String(p.stock), reason: "manual_correction" })} data-testid={`inv-adjust-${p.product_id}`} className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:border-blue-300 hover:text-blue-600" title="Adjust stock"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => openHistory(p)} data-testid={`inv-history-${p.product_id}`} className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:border-slate-300" title="History"><HistoryIcon className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adjust && <AdjustModal row={adjust} onClose={() => setAdjust(null)} onSaved={() => { setAdjust(null); load(); }} />}
      {histFor && <HistoryModal row={histFor} history={history} onClose={() => setHistFor(null)} />}
    </div>
  );
}

function AdjustModal({ row, onClose, onSaved }) {
  const [mode, setMode] = useState("new");
  const [value, setValue] = useState(String(row.stock));
  const [reason, setReason] = useState("manual_correction");
  const [threshold, setThreshold] = useState(String(row.threshold));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const body = { product_id: row.product_id, reason };
      if (mode === "new") body.new_stock = parseInt(value, 10);
      else body.delta = parseInt(value, 10);
      await api.post("/marketplace/seller/inventory/adjust", body);
      if (parseInt(threshold, 10) !== row.threshold) await api.put(`/marketplace/seller/inventory/${row.product_id}/threshold`, { low_stock_threshold: parseInt(threshold, 10) });
      toast.success("Inventory updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="inv-adjust-modal">
        <div className="mb-3 flex items-center justify-between"><p className="font-semibold text-slate-800">Adjust stock — {row.name}</p><button onClick={onClose}><X className="h-4 w-4 text-slate-400" /></button></div>
        <p className="mb-3 text-xs text-slate-500">Current: stock {row.stock} · reserved {row.reserved} · available {row.available}</p>
        <div className="mb-3 flex rounded-full border border-slate-200 p-0.5 text-xs font-semibold">
          <button onClick={() => setMode("new")} className={`flex-1 rounded-full py-1.5 ${mode === "new" ? "brand-gradient-bg text-white" : "text-slate-600"}`} data-testid="adjust-mode-new">Set to</button>
          <button onClick={() => setMode("delta")} className={`flex-1 rounded-full py-1.5 ${mode === "delta" ? "brand-gradient-bg text-white" : "text-slate-600"}`} data-testid="adjust-mode-delta">Add / remove</button>
        </div>
        <label className="mb-1 block text-xs font-semibold text-slate-600">{mode === "new" ? "New stock quantity" : "Change (+/-)"}</label>
        <Input type="number" value={value} onChange={(e) => setValue(e.target.value)} data-testid="adjust-value" />
        <label className="mb-1 mt-3 block text-xs font-semibold text-slate-600">Reason</label>
        <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="adjust-reason" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">{REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <label className="mb-1 mt-3 block text-xs font-semibold text-slate-600">Low-stock threshold</label>
        <Input type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)} data-testid="adjust-threshold" />
        <button onClick={save} disabled={saving} data-testid="adjust-save" className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full brand-gradient-bg py-2.5 text-sm font-semibold text-white disabled:opacity-60"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save adjustment"}</button>
      </div>
    </div>
  );
}

function HistoryModal({ row, history, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="inv-history-modal">
        <div className="mb-3 flex items-center justify-between"><p className="font-semibold text-slate-800">Inventory ledger — {row.name}</p><button onClick={onClose}><X className="h-4 w-4 text-slate-400" /></button></div>
        {history === null ? <div className="h-24 animate-pulse rounded-lg bg-slate-100" /> : history.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No movements recorded yet.</p> : (
          <div className="max-h-96 overflow-y-auto">
            {history.map((h) => (
              <div key={h.created_at + String(h.change)} className="flex items-center justify-between border-b border-slate-50 py-2 text-sm">
                <div><span className={`font-bold ${h.change > 0 ? "text-emerald-600" : h.change < 0 ? "text-rose-600" : "text-slate-500"}`}>{h.change > 0 ? "+" : ""}{h.change}</span> <span className="text-slate-500">{h.prev_stock} → {h.new_stock}</span></div>
                <div className="text-right"><p className="text-xs font-medium text-slate-700">{(h.reason || "").replace(/_/g, " ")}</p><p className="text-[10px] text-slate-400">{h.source} · {new Date(h.created_at).toLocaleString()}</p></div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
